import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Check, ArrowLeft } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import UserProfileMenu from '../components/UserProfileMenu';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '@/utils/env';
import { showAlert } from '@/components/AppDialog';

type ConsultReq = { topic: string; message: string; phone?: string; status: string; requestedAt: string };

const TOPICS = [
  'General consultation',
  'Fundraising & investor readiness',
  'Corporate Tax & VAT',
  'Legal & compliance',
  'Financial model / KPIs',
  'Company setup / visas',
  'Other',
];

const fmt = (s: string) => { try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return s; } };

export default function BookConsultation() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [params] = useSearchParams();

  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [existing, setExisting] = useState<ConsultReq[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const preset = params.get('topic');
    if (preset && TOPICS.includes(preset)) setTopic(preset);
  }, [params]);

  // Load any previous requests so we append rather than overwrite.
  useEffect(() => {
    (async () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (!u?.uid) return;
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${u.uid}`, { headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) } });
        if (resp.ok) { const j = await resp.json(); const arr = j.user?.consultationRequests; if (Array.isArray(arr)) setExisting(arr); }
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async () => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!u?.uid) { navigate('/login'); return; }
    setSaving(true);
    try {
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const next: ConsultReq[] = [...existing, { topic, message: message.trim(), phone: phone.trim(), status: 'new', requestedAt: new Date().toISOString() }];
      const resp = await fetch(`${API_BASE_URL}/auth/user/${u.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ consultationRequests: next }),
      });
      if (!resp.ok) throw new Error('Failed to send');
      setExisting(next);
      setDone(true);
    } catch {
      showAlert('Could not send your request. Please try again.');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white';

  return (
    <div className="flex min-h-screen bg-[#EFF4FB]">
      <Sidebar />
      <main className="flex-1 md:ms-64">
        <MobileNav onLogout={logout} />

        <header className="hidden md:flex items-center justify-between px-8 py-5 bg-white border-b border-gray-200">
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-600" /> Book a consultation</h1>
          <UserProfileMenu />
        </header>

        <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-800 mb-5">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>

          {done ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-black text-gray-900 mb-2">Request sent</h2>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed max-w-sm mx-auto">
                Thanks — our team has your request for <b>{topic}</b> and will reach out to schedule your consultation, usually within one business day.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => navigate('/dashboard')} className="rounded-xl bg-[#1D74F5] px-6 py-2.5 text-sm font-bold text-white hover:bg-blue-600 transition">Go to dashboard</button>
                <button onClick={() => { setDone(false); setMessage(''); }} className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 transition">Request another</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7">
              <h2 className="text-xl font-black text-gray-900 mb-1">Talk to a Conncct advisor</h2>
              <p className="text-sm text-gray-500 mb-6">Tell us what you'd like help with and we'll get back to you to arrange a call. It's free.</p>

              <label className="block text-xs font-bold text-gray-600 mb-1.5">What's it about?</label>
              <select value={topic} onChange={e => setTopic(e.target.value)} className={`${inputCls} mb-4`}>
                {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              <label className="block text-xs font-bold text-gray-600 mb-1.5">Anything we should know? <span className="font-medium text-gray-400">(optional)</span></label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="A sentence or two about what you need…" className={`${inputCls} mb-4 resize-y`} />

              <label className="block text-xs font-bold text-gray-600 mb-1.5">Best number to reach you <span className="font-medium text-gray-400">(optional)</span></label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 …" className={`${inputCls} mb-6`} />

              <button onClick={submit} disabled={saving} className="w-full rounded-xl bg-[#1D74F5] py-3 text-sm font-bold text-white hover:bg-blue-600 transition shadow-sm disabled:opacity-60">
                {saving ? 'Sending…' : 'Request consultation'}
              </button>
            </div>
          )}

          {existing.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 text-sm font-bold text-gray-900">Your requests</div>
              <div className="divide-y divide-slate-100">
                {[...existing].reverse().map((r, i) => (
                  <div key={i} className="px-6 py-3.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{r.topic}</div>
                      <div className="text-xs text-gray-400">{fmt(r.requestedAt)}</div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${r.status === 'scheduled' ? 'bg-green-50 text-green-700' : r.status === 'done' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>
                      {r.status === 'scheduled' ? 'Scheduled' : r.status === 'done' ? 'Done' : 'Requested'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
